<template>
  <SubscriptionNavigation
    @submit-clicked="onSubmit"
    :is-submitting-data="isSaveSubscriptionInProgress"
    :is-loading-data="isGetSubscriptionInProgress"
  >
    <div data-cy="electricity-consumption-page">
      <div class="text-center">
        <AtomText
          :text="t('subscription.electricityConsumption.title')"
          font-style="font-semibold"
          font-size="text-xl"
          data-cy="electricity-consumption-page-title"
        />
      </div>
      <div>
        <MoleculeLabel
          :text="t('subscription.electricityConsumption.form.radioGroupCards.label')"
          label-for="energy-informations"
          class="mb-2 mt-8"
          data-cy="mlh-equipments-annual-consumption-radio-label"
        />
        <OrganismRadioGroupCard
          v-model="currentAnnualConsumptionUnit"
          :cards="annualConsumptionUnitCards"
          name="annual-consumption"
          :error-message="t('common.forms.required')"
          :has-error="isSubmitted && !currentAnnualConsumptionUnit"
          data-cy="mlh-equipments-annual-consumption-radio-group-cards"
          orientation="horizontal"
        />

        <div
          v-for="annualConsumptionItem in annualConsumptionListItems"
          :key="annualConsumptionItem.id"
          class="my-2"
        >
          <div
            v-if="currentAnnualConsumptionUnit === annualConsumptionItem.id"
            data-cy="mlh-equipments-annual-consumption-input"
          >
            <MoleculeLabel
              :text="annualConsumptionItem.label"
              :label-for="annualConsumptionItem.currencyInput.id"
              mode-helper="tooltip"
              :tooltip-content="annualConsumptionItem.tooltip"
              class="mb-2 mt-6"
            />
            <MoleculeInputNumber
              v-if="currentAnnualConsumptionUnit === EAnnualConsumptionUnit.EUROS"
              addon-type="text"
              :addon-text="{
                value: annualConsumptionItem.currencyInput.currency.name,
                isPrefix: false,
              }"
              v-model="annualConsumptionItem.currencyInput.modelValue"
              :id="annualConsumptionItem.currencyInput.id"
              :name="annualConsumptionItem.currencyInput.id"
              :placeholder="annualConsumptionItem.currencyInput.placeholder"
              :helper="
                getHelperMessage({ errorMessage: annualConsumptionItem.currencyInput.errorMessage })
              "
              :input-state="
                getInputState({ errorMessage: annualConsumptionItem.currencyInput.errorMessage })
              "
              @error="onEuroError"
              @valid="onEuroValid"
            />

            <MoleculeInputNumber
              addon-type="text"
              :addon-text="{
                value: annualConsumptionItem.currencyInput.currency.name,
                isPrefix: false,
              }"
              v-if="currentAnnualConsumptionUnit === EAnnualConsumptionUnit.KILOWATT_HOURS"
              v-model="annualConsumptionItem.currencyInput.modelValue"
              :placeholder="annualConsumptionItem.currencyInput.placeholder"
              :name="annualConsumptionItem.currencyInput.id"
              :id="annualConsumptionItem.currencyInput.id"
              :helper="
                getHelperMessage({ errorMessage: annualConsumptionItem.currencyInput.errorMessage })
              "
              :input-state="
                getInputState({ errorMessage: annualConsumptionItem.currencyInput.errorMessage })
              "
              @error="onKwhError"
              @valid="onKwhValid"
            />
          </div>
        </div>
      </div>
    </div>
  </SubscriptionNavigation>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import useInputState from '@/composables/useInputState.composable';
import { useStepStore, useSubscriptionStore } from '@/stores';
import useToastSnackbarStore from '@/stores/toastSnackbar.store';

import type { AnnualConsumptionUnit } from '@/types/subscription.type';
import { getErrorToasterConfig } from '@/utils/error.util';

import SubscriptionNavigation from '@/components/SubscriptionNavigation.component.vue';

import { EAnnualConsumptionUnit } from '@/constants/subscription.constants';

const { t } = useI18n();

const subscriptionStore = useSubscriptionStore();
const stepStore = useStepStore();
const { addToast } = useToastSnackbarStore();
const { goToNextStep } = stepStore;

const { currentSubscription, isSaveSubscriptionInProgress, isGetSubscriptionInProgress } =
  storeToRefs(subscriptionStore);

const { getInputState, getHelperMessage } = useInputState();

const isSubmitted = ref(false);

const euroFieldErrors = ref<string[] | undefined>();
const isEuroFieldValid = ref(false);
const kwhFieldErrors = ref<string[] | undefined>();
const isKwhFieldValid = ref(false);

const onKwhError = (errors: string[]) => {
  isKwhFieldValid.value = false;
  kwhFieldErrors.value = errors;
};

const onKwhValid = () => {
  isKwhFieldValid.value = true;
  kwhFieldErrors.value = [];
};

const onEuroError = (errors: string[]) => {
  isEuroFieldValid.value = false;
  euroFieldErrors.value = errors;
};

const onEuroValid = () => {
  isEuroFieldValid.value = true;
  euroFieldErrors.value = [];
};

const euroTextError = computed(() => {
  if (isSubmitted.value) {
    if (euroFieldErrors.value?.includes('isEmpty')) {
      return t('common.forms.required');
    }

    if (euroFieldErrors.value?.includes('isNotANumber')) {
      return t('common.forms.format.type.number');
    }
  }

  return undefined;
});

const kwhTextError = computed(() => {
  if (isSubmitted.value) {
    if (kwhFieldErrors.value?.includes('isEmpty')) {
      return t('common.forms.required');
    }

    if (kwhFieldErrors.value?.includes('isNotANumber')) {
      return t('common.forms.format.type.number');
    }
  }

  return undefined;
});

const kwhAmount = ref<number | undefined>(
  currentSubscription.value.annualConsumption?.kilowattHours || undefined
);

const euroAmount = ref<number | undefined>(
  currentSubscription.value.annualConsumption?.euros || undefined
);

const annualConsumptionUnitCards = [
  {
    id: EAnnualConsumptionUnit.KILOWATT_HOURS,
    value: EAnnualConsumptionUnit.KILOWATT_HOURS,
    title: t(
      `subscription.electricityConsumption.form.radioGroupCards.units.${EAnnualConsumptionUnit.KILOWATT_HOURS}`
    ),
  },
  {
    id: EAnnualConsumptionUnit.EUROS,
    value: EAnnualConsumptionUnit.EUROS,
    title: t(
      `subscription.electricityConsumption.form.radioGroupCards.units.${EAnnualConsumptionUnit.EUROS}`
    ),
  },
];

const annualConsumptionListItems = ref([
  {
    id: EAnnualConsumptionUnit.KILOWATT_HOURS,
    label: t('subscription.electricityConsumption.form.unitInputs.kilowattHours.label'),
    tooltip: t('subscription.electricityConsumption.form.unitInputs.kilowattHours.tooltip'),
    currencyInput: {
      id: 'kwh-amount',
      modelValue: kwhAmount,
      errorMessage: kwhTextError,
      placeholder: t(
        'subscription.electricityConsumption.form.unitInputs.kilowattHours.placeholder'
      ),
      currency: {
        name: 'kWh',
        symbol: 'kWh',
      },
    },
  },
  {
    id: EAnnualConsumptionUnit.EUROS,
    label: t('subscription.electricityConsumption.form.unitInputs.euros.label'),
    tooltip: t('subscription.electricityConsumption.form.unitInputs.euros.tooltip'),
    currencyInput: {
      id: 'euro-amount',
      modelValue: euroAmount,
      errorMessage: euroTextError,
      placeholder: t('subscription.electricityConsumption.form.unitInputs.euros.placeholder'),
      currency: {
        name: 'EUR',
        symbol: '€',
      },
    },
  },
]);

const currentAnnualConsumptionUnit = ref<AnnualConsumptionUnit | null>(null);

const isFormValid = () => {
  if (currentAnnualConsumptionUnit.value === EAnnualConsumptionUnit.KILOWATT_HOURS) {
    return isKwhFieldValid.value;
  }
  return isEuroFieldValid.value;
};

const onSubmit = async () => {
  const isValid = isFormValid();
  isSubmitted.value = true;
  if (isValid) {
    try {
      const kwhValue =
        currentAnnualConsumptionUnit.value === EAnnualConsumptionUnit.KILOWATT_HOURS
          ? kwhAmount.value!
          : null;
      const euroValue =
        currentAnnualConsumptionUnit.value === EAnnualConsumptionUnit.EUROS
          ? euroAmount.value!
          : null;
      await subscriptionStore.updateSubcriptionConsumptionInformations({
        euros: euroValue,
        kilowattHours: kwhValue,
      });

      await goToNextStep();
    } catch (error) {
      const toastConfig = getErrorToasterConfig({
        error,
        toasterId: 'electricity-consumption',
        defaultMessage: t('subscription.equipments.api.errors.patchSubscriptionConsumption'),
      });

      addToast(toastConfig);
    }
  }
};

onMounted(async () => {
  const annualConsumption = currentSubscription.value.annualConsumption;

  if (annualConsumption?.euros) {
    currentAnnualConsumptionUnit.value = EAnnualConsumptionUnit.EUROS;
    return;
  }
  if (annualConsumption?.kilowattHours) {
    currentAnnualConsumptionUnit.value = EAnnualConsumptionUnit.KILOWATT_HOURS;
    return;
  }

  currentAnnualConsumptionUnit.value = null;
});
</script>

<style scoped></style>
